import {
  Component,
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { Navigate, useLocation, useNavigationType, useParams } from "react-router-dom";

import { applyMetadata } from "./metadata";
import { NotFoundError, RedirectError } from "./navigation";
import type { Metadata } from "./types";

// Each page declares its own params shape; values come from the path.
type PageProps = { params: Promise<any> };

type PageModule = {
  // Redirect-only pages render nothing (they always throw).
  default: (props: PageProps) => ReactNode | void | Promise<ReactNode | void>;
  metadata?: Metadata;
  generateMetadata?: (props: PageProps) => Promise<Metadata>;
};

const pending = <main className="min-h-screen bg-[#080318]" />;

// Next.js' built-in 404 page.
export function NotFoundPage() {
  useEffect(() => {
    applyMetadata(undefined);
  }, []);

  return (
    <div style={{ fontFamily: 'system-ui,"Segoe UI",Roboto,Helvetica,Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji"', height: "100vh", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <div>
        <style>{"body{color:#000;background:#fff;margin:0}.next-error-h1{border-right:1px solid rgba(0,0,0,.3)}@media (prefers-color-scheme:dark){body{color:#fff;background:#000}.next-error-h1{border-right:1px solid rgba(255,255,255,.3)}}"}</style>
        <h1 className="next-error-h1" style={{ display: "inline-block", margin: "0 20px 0 0", padding: "0 23px 0 0", fontSize: 24, fontWeight: 500, verticalAlign: "top", lineHeight: "49px" }}>404</h1>
        <div style={{ display: "inline-block" }}>
          <h2 style={{ fontSize: 14, fontWeight: 400, lineHeight: "49px", margin: 0 }}>This page could not be found.</h2>
        </div>
      </div>
    </div>
  );
}

// Handles notFound()/redirect() thrown while rendering a page.
export class PageBoundary extends Component<{ children: ReactNode }, { error: unknown }> {
  state = { error: null as unknown };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  render() {
    const { error } = this.state;
    if (error instanceof RedirectError) return <Navigate to={error.url} replace />;
    if (error instanceof NotFoundError) return <NotFoundPage />;
    if (error) {
      return (
        <main className="flex min-h-screen items-center justify-center bg-[#080318] px-6 text-center text-white">
          <p className="text-sm text-white/60">Application error: a client-side exception has occurred.</p>
        </main>
      );
    }
    return this.props.children;
  }
}

// Former server components: awaited here, then rendered.
function AsyncPage({ page, params }: { page: PageModule["default"]; params: Record<string, string> }) {
  const key = JSON.stringify(params);
  const [result, setResult] = useState<{ key: string; node?: ReactNode; error?: unknown }>();

  useEffect(() => {
    let active = true;
    Promise.resolve()
      .then(() => page({ params: Promise.resolve(JSON.parse(key)) }))
      .then(
        (node) => active && setResult({ key, node: node as ReactNode }),
        (error) => active && setResult({ key, error: error ?? new Error("Page failed") })
      );
    return () => {
      active = false;
    };
  }, [page, key]);

  if (!result || result.key !== key) return pending;
  if (result.error) throw result.error;
  return <>{result.node}</>;
}

function PageHost({ module, isAsync }: { module: PageModule; isAsync: boolean }) {
  const params = useParams() as Record<string, string>;
  const key = JSON.stringify(params);

  useEffect(() => {
    let active = true;
    const props = { params: Promise.resolve(JSON.parse(key)) };
    Promise.resolve(module.generateMetadata ? module.generateMetadata(props) : module.metadata)
      .then((metadata) => active && applyMetadata(metadata))
      .catch(() => active && applyMetadata(undefined));
    return () => {
      active = false;
    };
  }, [module, key]);

  if (isAsync) {
    return <AsyncPage page={module.default} params={params} />;
  }
  const Page = module.default as ComponentType;
  return <Page />;
}

export function page(load: () => Promise<PageModule>, options: { async?: boolean } = {}) {
  const Lazy = lazy(() =>
    load().then((module) => ({
      default: () => <PageHost module={module} isAsync={Boolean(options.async)} />,
    }))
  );

  return (
    <Suspense fallback={pending}>
      <Lazy />
    </Suspense>
  );
}

// Next.js scrolls to the top (or to the #hash target) after navigating.
export function ScrollManager() {
  const { pathname, hash, state } = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    if (navigationType === "POP" || (state as { preserveScroll?: boolean } | null)?.preserveScroll) return;
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = decodeURIComponent(hash.slice(1));
    let attempts = 0;
    const timer = window.setInterval(() => {
      const target = document.getElementById(id);
      if (target || ++attempts > 40) {
        window.clearInterval(timer);
        target?.scrollIntoView();
      }
    }, 50);
    return () => window.clearInterval(timer);
  }, [pathname, hash, state, navigationType]);

  return null;
}
